import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { AnalyticalDashboardService } from 'src/app/services/analytical-dashboard.service';
import { ConfidenceMonitoringService } from 'src/app/services/confidence-monitoring.service';
import { Utility } from 'src/app/utils/app-utils';
import { UserSessionStateService } from 'src/app/services/user-session-state.service';
import { Subscription } from 'rxjs';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

@Component({
	selector: 'app-sites-table',
	templateUrl: './sites-table.component.html',
	styleUrls: ['./sites-table.component.scss']
})
export class SitesTableComponent implements OnInit, OnDestroy, AfterViewInit {

	public branchDetailDataSource: MatTableDataSource<any> = new MatTableDataSource([]);
	public selectedBranchId = null;
	public selectedBranch: any;
	public selectedSitesAsset = this.analyticalDashboard.sitesAsset.MAP;
	public branchSearchText = '';
	@Input() displayedColumns = [];
	@Output() selectedBranchEvent = new EventEmitter<any>();
	branchChangeSubscriber: Subscription;
	private allBranchDetailDataSource: any[] = [];
	@ViewChild(MatSort, { static: false }) sort: MatSort;

	constructor(
		public analyticalDashboard: AnalyticalDashboardService,
		public confidenceMonitoringService: ConfidenceMonitoringService,
		public userSessionStateService: UserSessionStateService
	) { }

	async ngOnInit() {
		await this.getBranchStatus();

		this.branchChangeSubscriber = this.analyticalDashboard.getBranchNotification().subscribe(() => {
			this.selectBranch();
		});

		this.analyticalDashboard.getSitesSelectedAssetDocs().subscribe((selectedSitesAsset) => {
			this.selectedSitesAsset = selectedSitesAsset;
		});
	}

	ngAfterViewInit() {
		setTimeout(() => {
			this.connectSort();
		}, 0);
	}

	/**
	 * Normalize string for sorting - same logic as sortBranchesAlphabetically
	 * Removes spaces and converts to lowercase for natural numeric sorting
	 */
	private normalizeForSorting(str: string): string {
		return (str || '').toString().replace(/\s+/g, '').toLowerCase();
	}

	/**
	 * Connect MatSort to the data source and configure custom(alphabetical and numeric) sorting
	 */
	private connectSort() {
		// Configure sortingDataAccessor for all columns
		this.branchDetailDataSource.sortingDataAccessor = (item, property) => {
			let value: any;
			switch (property) {
				case 'Region':
					value = this.normalizeForSorting(item.areaName);
					break;
				case 'Sites':
					value = this.normalizeForSorting(item.branchName);
					break;
				case 'Site Priority':
					value = (item.branchType || '').toString().toLowerCase();
					break;
				case 'Operator':
					value = this.formatOperatorsCounter(item.operators) || 0;
					break;
				case 'Status':
					value = (item.status || '').toString().toLowerCase();
					break;
				case 'Asset':
					value = this.formatAssetsCounter(item.devices) || 0;
					break;
				default:
					value = item[property];
					if (typeof value === 'string') {
						value = value.toLowerCase();
					}
			}
			return value;
		};

		if (this.sort) {
			// Override sortData to use localeCompare with numeric option for Region and Sites
			// This matches the logic from sortBranchesAlphabetically
			const originalSortData = (this.branchDetailDataSource as any).sortData;
			(this.branchDetailDataSource as any).sortData = (data: any[], sort: MatSort) => {
				if (!sort.active || !sort.direction) {
					return data;
				}

				// For Region and Sites columns, use localeCompare with numeric option
				if (sort.active === 'Region' || sort.active === 'Sites') {
					return [...data].sort((a, b) => {
						const normalizedA = this.normalizeForSorting(
							sort.active === 'Region' ? a.areaName : a.branchName
						);
						const normalizedB = this.normalizeForSorting(
							sort.active === 'Region' ? b.areaName : b.branchName
						);

						const comparison = normalizedA.localeCompare(normalizedB, undefined, {
							numeric: true,
							sensitivity: 'base'
						});

						return sort.direction === 'asc' ? comparison : -comparison;
					});
				}

				// For other columns, use default sorting
				return originalSortData ? originalSortData.call(this.branchDetailDataSource, data, sort) : data;
			};

			this.branchDetailDataSource.sort = this.sort;
		}
	}

	ngOnDestroy() {
		Utility.Unsubscribe(this.branchChangeSubscriber);
	}

	selectBranch() {
		const data = this.analyticalDashboard.branchDataSource ? this.analyticalDashboard.branchDataSource : [];
		this.updateBranchDataSource(data, true);
	}

	async getBranchStatus() {
		const list = await this.analyticalDashboard.getBranchStatusReport();
		this.updateBranchDataSource(list, true);
	}

	/**
	 * format assets name
	 */
	formatAssetsNames(assets: any) {
		return assets ? assets.map(asset => asset.name).join(', ') : '';
	}

	/**
	 * get assets count
	 */
	formatAssetsCounter(assets: any) {
		return assets ? assets.length : 0;
	}

	/**
	 * format operators name
	 */
	formatOperatorsNames(operators: any) {
		return operators ? operators.map(operator => `${operator.firstName} ${operator.lastName}`).join(', ') : '';
	}

	/**
	 * get operators count
	 */
	formatOperatorsCounter(operator: any) {
		return operator ? operator.length : 0;
	}

	/**
	 * Sort branches alphabetically by branchName with natural numeric sorting
	 * Requirements:
	 * 1. Sort alphabetically (A–Z)
	 * 2. Ignore case sensitivity
	 * 3. Ignore spaces entirely
	 * 4. Apply natural numeric sorting
	 * 5. Stable sort for duplicates
	 */
	sortBranchesAlphabetically(branches: any[]): any[] {
		return branches.sort((a, b) => {
			// Remove all spaces and convert to lowercase for comparison
			const nameA = a.branchName.replace(/\s+/g, '').toLowerCase();
			const nameB = b.branchName.replace(/\s+/g, '').toLowerCase();

			// Use localeCompare with numeric option for natural sorting
			// This handles numeric parts correctly (1 < 2 < 10)
			const comparison = nameA.localeCompare(nameB, undefined, {
				numeric: true,
				sensitivity: 'base' // Ignore case and accents
			});

			// If names are identical after normalization, maintain original order (stable sort)
			// This ensures duplicates keep their input order
			return comparison;
		});
	}

	/**
	 * select and highlight a row
	 */
	highlight(row: any) {
		this.selectedBranchId = row.id;
		this.selectedBranch = row;
		this.analyticalDashboard.selectAlarm(this.selectedBranch);
		setTimeout(() => {
			this.confidenceMonitoringService.changeSelectedSite(this.selectedBranch);
		}, 200);

		this.selectedBranchEvent.emit(this.selectedBranch);
	}

	onSearchInputChange() {
		this.applySearchFilter(false);
	}

	private updateBranchDataSource(branches: any[], emitSelection: boolean) {
		const normalizedBranches = Array.isArray(branches) ? [...branches] : [];
		this.allBranchDetailDataSource = this.sortBranchesAlphabetically(normalizedBranches);
		this.applySearchFilter(emitSelection);
	}

	private applySearchFilter(emitSelection: boolean) {
		const searchTerm = this.branchSearchText.trim().toLowerCase();
		if (!searchTerm) {
			this.branchDetailDataSource.data = [...this.allBranchDetailDataSource];
		} else {
			this.branchDetailDataSource.data = this.allBranchDetailDataSource.filter(branch => {
				const branchName = (branch?.branchName || '').toLowerCase();
				const areaName = (branch?.areaName || '').toLowerCase();
				return branchName.includes(searchTerm) || areaName.includes(searchTerm);
			});
		}

		setTimeout(() => {
			this.connectSort();
		}, 0);
		this.ensureBranchSelection(emitSelection);
	}

	private ensureBranchSelection(forceHighlightFirst: boolean) {
		if (!this.branchDetailDataSource.data.length) {
			return;
		}

		if (forceHighlightFirst) {
			this.highlight(this.branchDetailDataSource.data[0]);
			return;
		}

		const existingSelection = this.branchDetailDataSource.data.find(branch => branch.id === this.selectedBranchId);
		if (!existingSelection) {
			this.highlight(this.branchDetailDataSource.data[0]);
		}
	}

}
